import {isInvalidTag} from '@wandb/weave/common/util/tags';
import {Select} from '@wandb/weave/components/Form/Select';
import {Icon} from '@wandb/weave/components/Icon';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {ChangeEvent, useEffect, useState} from 'react';
import {MultiValue, SingleValue} from 'react-select';
import {CollectionInfo} from 'src/components/ModelRegistry/utils';

import {useRegistryProjectArtifactTypesQuery} from '../../../../generated/graphql';
import {validateArtifactName} from '../../../../util/artifacts';
import {propagateErrorsContext} from '../../../../util/errors';
import {TagType} from '../../../Tags';
import {InstrumentedLoader as Loader} from '../../../utility/InstrumentedLoader';
import {ArtifactTypeInfoBanner} from '../../common/ArtifactTypeInfoBanner';
import {CreatableSelect} from './CreatableSelect';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {Collection} from './CreateRegisteredCollectionDrawerInner';
import * as S from './CreateRegisteredCollectionDrawerInner.styles';
import {ErrorText} from './ErrorText';
import {RegisteredModelLimitReachedBanner} from './RegisteredModelLimitReachedBanner';

type GenericOptionType = {
  value: string;
  label: string;
};

type DrawerPageCollectionConfigurationInnerProps = {
  collection: Collection;
  showModelLimitReached: boolean;
  entityTagsList: string[];
  existingCollections: CollectionInfo[];
  isInvalidCollectionName: boolean;
  setIsInvalidCollectionName: (val: boolean) => void;
  setCollection: (val: Collection) => void;
  defaultArtifactTypeName?: string;
  isInvalidArtifactTypeName?: boolean;
  setIsInvalidArtifactTypeName?: (val: boolean) => void;
  cannotCreateArtifactType?: boolean;
  registryAllowsAllArtifactTypes?: boolean;
};

export const DrawerPageCollectionConfigurationInner = ({
  collection,
  showModelLimitReached,
  entityTagsList,
  isInvalidCollectionName,
  existingCollections,
  setIsInvalidCollectionName,
  setCollection,
  defaultArtifactTypeName,
  isInvalidArtifactTypeName,
  setIsInvalidArtifactTypeName,
  cannotCreateArtifactType,
  registryAllowsAllArtifactTypes = true,
}: DrawerPageCollectionConfigurationInnerProps) => {
  const [collectionNameExists, setCollectionNameExists] = useState(false);
  const entityTagsOptions = entityTagsList.map((tag: string) => {
    return {
      value: tag,
      label: tag,
    };
  });
  useEffect(() => {
    const existingCollectionName = existingCollections
      ?.filter(
        c =>
          c.entityName === collection.entityName &&
          c.projectName === collection.projectName
      )
      .some(c => c?.collectionName === collection.name);
    if (existingCollectionName) {
      setCollectionNameExists(true);
    } else {
      setCollectionNameExists(false);
    }
  }, [
    existingCollections,
    collection.entityName,
    collection.projectName,
    collection.name,
  ]);

  // Get the list of artifact types in this project
  const {data: artifactTypesData, loading} =
    useRegistryProjectArtifactTypesQuery({
      variables: {
        entityName: collection.entityName,
        projectName: collection.projectName,
      },
      context: propagateErrorsContext(),
      skip: cannotCreateArtifactType,
    });
  const artifactTypeEdges =
    artifactTypesData?.project?.artifactTypes?.edges ?? [];
  const artifactTypeNames = artifactTypeEdges.map(edge => edge?.node?.name);
  const artifactTypeOptions =
    artifactTypeNames.map(name => ({
      value: name ?? '',
      label: name ?? '',
    })) ?? [];

  if (showModelLimitReached) {
    return <RegisteredModelLimitReachedBanner />;
  }
  if (loading) {
    return (
      <Loader
        name="create-collection-load-registry-artifact-types"
        inline
        size="large"
        samplingRate={1}
      />
    );
  }
  return (
    <>
      <S.LabelRow>
        <S.Label>Name</S.Label>
      </S.LabelRow>
      <div data-test="name-input">
        <S.StyledTextField
          placeholder="Name your collection"
          value={collection.name ?? ''}
          onChange={value => {
            // validation
            if (validateArtifactName(value)) {
              setIsInvalidCollectionName?.(false);
            } else {
              setIsInvalidCollectionName?.(true);
            }
            setCollection({...collection, name: value});
          }}
          autoFocus={collection.entityName !== ''}
          errorState={isInvalidCollectionName}
        />
      </div>
      {isInvalidCollectionName && (
        <ErrorText>
          Collection names can only contain the following special characters: _,
          -, .
        </ErrorText>
      )}
      {collectionNameExists && (
        <ErrorText>The name "{collection.name}" already exists</ErrorText>
      )}
      <S.LabelRow>
        <S.Label>Type</S.Label>
        <Tooltip
          trigger={<Icon name="info" style={{marginLeft: '8px'}} />}
          content="This defines the type of artifacts that can be linked to this collection. For example, if you select “my-type” from the dropdown, only artifacts of type=”my-type” can be linked to this collection."
        />
      </S.LabelRow>
      <div data-test="artifact-type-input">
        {registryAllowsAllArtifactTypes ? (
          <>
            <CreatableSelect<GenericOptionType, false>
              options={artifactTypeOptions}
              size="medium"
              onChange={(option: SingleValue<GenericOptionType>) => {
                if (option?.value != null) {
                  if (
                    defaultArtifactTypeName != null &&
                    !option.value
                      .toLowerCase()
                      .includes(defaultArtifactTypeName)
                  ) {
                    setIsInvalidArtifactTypeName?.(true);
                  } else {
                    setIsInvalidArtifactTypeName?.(false);
                  }
                  setCollection({
                    ...collection,
                    artifactTypeName: option.value,
                  });
                }
              }}
              value={
                collection.artifactTypeName !== ''
                  ? {
                      value: collection.artifactTypeName,
                      label: collection.artifactTypeName,
                    }
                  : null
              }
              placeholder="Select the type of artifacts to link to this collection"
              isDisabled={cannotCreateArtifactType}
            />
            {collection.artifactTypeName !== '' &&
              !artifactTypeNames.includes(collection.artifactTypeName) && (
                <ArtifactTypeInfoBanner />
              )}
          </>
        ) : (
          <Select<GenericOptionType, false>
            value={
              collection.artifactTypeName !== ''
                ? {
                    value: collection.artifactTypeName,
                    label: collection.artifactTypeName,
                  }
                : null
            }
            options={artifactTypeOptions}
            placeholder={'Select artifact types'}
            onChange={(option: SingleValue<GenericOptionType>) => {
              if (option != null) {
                setCollection({
                  ...collection,
                  artifactTypeName: option.value,
                });
              }
            }}
          />
        )}
      </div>
      {isInvalidArtifactTypeName && defaultArtifactTypeName != null && (
        <ErrorText>
          Type name must contain the word "{defaultArtifactTypeName}"
        </ErrorText>
      )}
      <S.LabelRow>
        <S.Label>
          Description <S.LabelSpan>(optional)</S.LabelSpan>
        </S.Label>
        <Tooltip
          trigger={<Icon name="info" style={{marginLeft: '8px'}} />}
          content="The description will be rendered in markdown format in the W&B UI."
        />
      </S.LabelRow>
      <S.StyledTextArea
        value={collection.description ?? ''}
        placeholder="Describe your collection"
        onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
          setCollection({...collection, description: event.target.value})
        }
      />
      <S.LabelRow>
        <S.Label>Tags</S.Label>
        <Tooltip
          trigger={<Icon name="info" style={{marginLeft: '8px'}} />}
          content="Tags are like keywords or labels that help organize and group collections. By assigning tags to one or more collections, you create handy groups for efficient searching."
        />
      </S.LabelRow>
      <CreatableSelect<GenericOptionType, true>
        options={entityTagsOptions}
        onChange={(options: MultiValue<GenericOptionType>, _: any) =>
          setCollection({
            ...collection,
            tags: options.map(option => option.value),
          })
        }
        isMulti={true}
        value={collection.tags?.map(tag => {
          return {
            value: tag,
            label: tag,
          };
        })}
        onInputChange={(value: string) => {
          // tag validation
          const regExp = /[^-:\w\s]/gi;
          if (isInvalidTag(value)) {
            return value.replace(regExp, '');
          }
          return value;
        }}
        placeholder="Type to add tags (Ex: Model task, architecture, company, geography)"
        tagType={TagType.TAG}
      />
    </>
  );
};
